import { WorkLogsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as WorkLogsActions from './work-logs.actions';

@Injectable()
export class WorkLogsEffects {
  private actions$ = inject(Actions);
  private workLogsApi = inject(WorkLogsApiService);
  private notification = inject(NotificationService);

  loadWorkLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkLogsActions.loadWorkLogs),
      switchMap(({ params }) =>
        this.workLogsApi.findAll(params).pipe(
          map(response => WorkLogsActions.loadWorkLogsSuccess({
            workLogs: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.load_work_logs_error');
            }
            return of(WorkLogsActions.loadWorkLogsFailure({ error: errorMessage || 'Failed to load work logs' }));
          }),
        ),
      ),
    ),
  );

  adminCreateWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkLogsActions.adminCreateWorkLog),
      switchMap(({ workLogData }) =>
        this.workLogsApi.adminCreate(workLogData).pipe(
          map((workLog) => {
            this.notification.success('admin.notifications.work_log_created');
            return WorkLogsActions.adminCreateWorkLogSuccess({ workLog });
          }),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.create_work_log_error');
            }
            return of(WorkLogsActions.adminCreateWorkLogFailure({ error: errorMessage || 'Failed to create work log' }));
          }),
        ),
      ),
    ),
  );

  approveWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkLogsActions.approveWorkLog),
      switchMap(({ id, approveData }) =>
        this.workLogsApi.approve(id, approveData).pipe(
          map((workLog) => {
            this.notification.success('admin.notifications.work_log_approved');
            return WorkLogsActions.approveWorkLogSuccess({ workLog });
          }),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.approve_work_log_error');
            }
            return of(WorkLogsActions.approveWorkLogFailure({ error: errorMessage || 'Failed to approve work log' }));
          }),
        ),
      ),
    ),
  );

  rejectWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkLogsActions.rejectWorkLog),
      switchMap(({ id, rejectData }) =>
        this.workLogsApi.reject(id, rejectData).pipe(
          map((workLog) => {
            this.notification.success('admin.notifications.work_log_rejected');
            return WorkLogsActions.rejectWorkLogSuccess({ workLog });
          }),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.reject_work_log_error');
            }
            return of(WorkLogsActions.rejectWorkLogFailure({ error: errorMessage || 'Failed to reject work log' }));
          }),
        ),
      ),
    ),
  );

  updateWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkLogsActions.updateWorkLog),
      switchMap(({ id, updateData }) =>
        this.workLogsApi.update(id, updateData).pipe(
          map((workLog) => {
            this.notification.success('admin.notifications.work_log_updated');
            return WorkLogsActions.updateWorkLogSuccess({ workLog });
          }),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.update_work_log_error');
            }
            return of(WorkLogsActions.updateWorkLogFailure({ error: errorMessage || 'Failed to update work log' }));
          }),
        ),
      ),
    ),
  );

  deleteWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkLogsActions.deleteWorkLog),
      switchMap(({ id }) =>
        this.workLogsApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.work_log_deleted');
            return WorkLogsActions.deleteWorkLogSuccess({ id });
          }),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.delete_work_log_error');
            }
            return of(WorkLogsActions.deleteWorkLogFailure({ error: errorMessage || 'Failed to delete work log' }));
          }),
        ),
      ),
    ),
  );
}
